import { createLogger } from '@telia-ace/edge-log';
import { getUrlParam, getConfig } from '@telia-ace/websdk-utilities';
import { getXDItem, setXDItem } from '@telia-ace/websdk-xd-storage';

import * as chatState from '../chatState';
import * as chatEngine from '../chatEngine';
import { callEventHandler } from '../clientInterface';
import * as videoWindowController from './windowController';
import { insightsEvent } from '../utils/chatUtils';

const log = createLogger('video/opentok');

const OPENTOK_LOG_LEVEL_URL_PARAM_NAME = 'videoLogLevel';

const NETWORK_DISCONNECTED_REASON = 'networkDisconnected';

const STYLE_PROPERTIES = {
  nameDisplayMode: 'off', // Show video stream name in OT video element?
  buttonDisplayMode: 'off', // Show mute audio buttons in OT video element?
  audioLevelDisplayMode: 'off', // Show audio level indicator in OT video element?
};

const SUBSCRIBER_PROPERTIES = {
  insertMode: 'append',
  fitMode: 'contain',
  width: '100%',
  height: '100%',
  style: STYLE_PROPERTIES,
};

const PUBLISHER_PROPERTIES = {
  insertMode: 'append',
  fitMode: 'contain',
  width: '100%',
  height: '100%',
  style: STYLE_PROPERTIES,
  facingMode: 'user',
  videoContentHint: 'detail',
};

/**
 * Errors (or error categories) below represent all OpenTok errors that can occur.
 * Chat client also gets to know OpenTok error name and can choose to handle
 * things with a finer grain of precision if needed.
 */
const errors = {
  DEVICE_ACCESS_DISMISSED: 'DEVICE_ACCESS_DISMISSED',
  DEVICE_ACCESS_DENIED: 'DEVICE_ACCESS_DENIED',
  DEVICE_DISABLED: 'DEVICE_DISABLED',
  DEVICE_NOT_FOUND: 'DEVICE_NOT_FOUND',
  DEVICE_UNAVAILABLE: 'DEVICE_UNAVAILABLE',
  DEVICE_OTHER_ERROR: 'DEVICE_OTHER_ERROR',
  CONNECT_ERROR: 'CONNECT_ERROR',
  PUBLISH_ERROR: 'PUBLISH_ERROR',
  SUBSCRIBE_ERROR: 'SUBSCRIBE_ERROR',
  SESSION_ERROR: 'SESSION_ERROR',
  NETWORK_ERROR: 'NETWORK_ERROR',
  BROWSER_ERROR: 'BROWSER_ERROR',
  INTERNAL_ERROR: 'INTERNAL_ERROR',
  UNEXPECTED_ERROR: 'UNEXPECTED_ERROR',
};

/**
 * Actions below represent all recommended actions to repair a video error.
 * Example scenario: Chat client receives a video status info message with action
 * 'RELOAD_VIDEO_PUBLISHER' and can show a button "Repair video" that will call
 * CallGuide.api.chat.reloadVideoPublisher.
 */
const actions = {
  // Use case: Some (Chrome) browser bugs requires the browser to be shut down and restarted
  RESTART_BROWSER: 'RESTART_BROWSER',

  // Use case: Video is severely broken, reloading web page will setup the chat from start again.
  RELOAD_PAGE: 'RELOAD_PAGE',

  // Use case: Visitor blocked video camera and need the browser permission dialog to show again.
  RELOAD_PUBLISHER: 'RELOAD_PUBLISHER',

  // Use case: Video is broken, disconnect and then reconnect to OpenTok session again may solve
  // the problem without needing to reload the web page.
  RELOAD_SESSION: 'RELOAD_SESSION',

  // Use case: OpenTok video session has expired, or similar problems. New session is created by
  // stopping and starting a new video from ACE Interact.
  RENEW_SESSION: 'RENEW_SESSION',
};

// Globals ----------------------------------------------------------------------

let OT;

const state = {
  providerIsInitialized: false,
  session: undefined,
  publisher: undefined,
  muteVideo: undefined,
  muteMicrophone: undefined,
  deviceId: undefined,
  subscriptions: [], // Book-keeping of subscriptions, streamID and DOM elements
};

// INIT  ----------------------------------------------------------------------

/**
 * Initialize video provider.
 * This should only be done once: when chat client request video chat, not before that!
 */
async function initProvider() {
  if (state.providerIsInitialized) {
    return;
  }
  await initState();
  await initOpenTok();
  initProxy();
  initLogging();
  initSubscriptions();
  initVideoWindowController();
  state.providerIsInitialized = true;
}

async function initOpenTok() {
  OT = await import('@opentok/client');
  OT.on('exception', handleExceptions);
}

/**
 * Initialize OpenTok proxy.
 * This should be done before all other OT methods!
 */
function initProxy() {
  const ipProxy = getConfig('cht').video.ipProxy;
  if (ipProxy) {
    log.debug('Setting OpenTok IP Proxy:', ipProxy);
    OT.setProxyUrl(ipProxy);
  }
}

// SETUP VIDEO ----------------------------------------------------------------------

/**
 * Setup OpenTok video.
 */
export async function setupVideo({ apiKey, sessionId, token, infoText, agent }) {
  log.trace('Setup video, params:', apiKey, sessionId, token, infoText, agent);
  await initProvider();
  initChatClient(infoText, agent);
  startVideo();
}

export async function chatButtonClicked(args) {
  log.debug('chatButtonClicked not applicable for this video provider!', args);
}

export async function endVideoButtonClicked(args) {
  log.debug('chatButtonClicked not applicable for this video provider!', args);
}

/**
 * Start OpenTok video.
 */
function startVideo() {
  initSession();
}

/**
 * Reload OpenTok video session.
 * Disconnect any active video and then start it again.
 */
export function reloadVideoSession() {
  log.trace('reloadVideoSession, state:', state);

  if (state.publisher) {
    state.publisher.destroy();
  }

  initSubscriptions();

  const sessionIsExisting = state.session && state.session.disconnect;
  const sessionIsConnected = sessionIsExisting && state.session.isConnected();

  if (sessionIsConnected) {
    // Remove current event handlers to avoid normal disconnect handling!
    // Start video after session has been properly disconnected.
    state.session.off();
    state.session.on('sessionDisconnected', () => {
      handleSessionDisconnected();
      startVideo();
    });
    state.session.disconnect();
  } else if (sessionIsExisting) {
    // Remove current event handlers to avoid normal disconnect handling!
    // No connected session, no need to wait for disconnected event, start video right away!
    state.session.off();
    state.session.disconnect();
    handleSessionDisconnected();
    startVideo();
  } else {
    // No existing session, start video right away!
    handleSessionDisconnected();
    startVideo();
  }
}

/**
 * Reload OpenTok video publisher.
 */
export function reloadVideoPublisher() {
  log.trace('reloadVideoPublisher, state:', state);
  if (state.publisher) {
    state.publisher.destroy();
  }
  publishToSession(state.session);
}

// STOP VIDEO ----------------------------------------------------------------------

/**
 * These functions dealing with stopping and suspending video have been crafted during the years
 * to work with quite complex logic to handle a rather dysfunctional refchat video client.
 * If needed to refactor, refactor them with care!!
 */

export function stopVideo() {
  if (!chatState.get('videoConnected')) {
    log.debug('Video is not connected, ignore stopVideo signal!');
    return;
  }

  tellChatClientToStopVideo();
  disconnectVideoWindowController();
  disconnectSession();

  if (state.session) {
    insightsEvent('endVideo');
  }
}

export function disconnectSession() {
  if (state.session) {
    state.session.off();
    state.session.disconnect();
  }

  clearSubscriptions();

  if (state.publisher) {
    state.publisher.destroy();
  }
}

export function suspendVideo({ infoText, agent }) {
  tellChatClientToSuspendVideo(infoText, agent);
}

export function sendStopVideoToClient() {
  tellChatClientToStopVideo();
}

// VIDEO COMMANDS -----------------------------------------------------------------

/**
 * Mute speaker audio for video call.
 *
 * This feature is deprecated and removed in WebSDK 22.
 * If we remove the code we can also remove the videoConnectionFailed state property and make
 * video connection handling much simpler. Since there will be no customers using
 * old video code when WebSDK 22 is released we can as well remove it.
 */
export function muteSpeaker() {
  log.warn('Received request to mute speakers. This feature is deprecated and removed!');
}

export function muteMicrophone({ flag }) {
  log.trace(`Mute or unmute microphone: ${flag ? 'Mute' : 'Unmute'}`);
  setMuteMicrophoneState(flag);
  if (state.publisher) {
    state.publisher.publishAudio(!state.muteMicrophone);
  } else {
    // When publisher is created, it will mute according to state properties!
  }
}

export function muteVideo({ flag }) {
  log.trace(`Mute or unmute video: ${flag ? 'Mute' : 'Unmute'}`);
  setMuteVideoState(flag);
  if (state.publisher) {
    state.publisher.publishVideo(!state.muteVideo);
  } else {
    // When publisher is created, it will mute according to state properties!
  }
}

export function cycleVideo() {
  state.publisher
    .cycleVideo()
    .then(({ deviceId }) => {
      log.trace('Switch camera, new deviceId:', deviceId);
      setDeviceIdState(deviceId);
    })
    .catch((error) => log.error('Failed switching camera in video chat. Error:', error));
}

// SESSION ----------------------------------------------------------------------

function initSession() {
  const apiKey = chatState.get('ot_apiKey');
  const sessionId = chatState.get('ot_sessionId');
  const token = chatState.get('ot_token');
  log.trace('Init OpenTok session (apiKey, sessionId, token):', apiKey, sessionId, token);

  const options = getConfig('cht').video.openTokInitSessionOptions || {};
  const session = OT.initSession(apiKey, sessionId, options);
  setSessionState(session);

  log.debug('OpenTok session initialized:', options, session);

  session.on('connectionCreated', (event) => {
    /**
     * Dispatched when a new client (including your own) has connected to the session,
     * and for every client in the session when you first connect. (The Session object also
     * dispatches a sessionConnected event when your local client connects.)
     *
     * WEBSDK: No need to do anything here (since action is taken in sessionConnected),
     * but logging might be useful.
     */
    log.trace('Received OpenTok connectionCreated Session event:', event);
  });

  session.on('connectionDestroyed', (event) => {
    /**
     * A client, other than your own, has disconnected from the session.
     * WEBSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.trace('Received OpenTok connectionDestroyed Session event:', event);
  });

  session.on('sessionConnected', (event) => {
    /**
     * The client has connected to an OpenTok session.
     * WEBSDK: Treat video as connected when this event occurs!
     */
    log.trace('Received OpenTok sessionConnected Session event:', event);
    handleSessionConnected();
  });

  session.on('sessionReconnecting', (event) => {
    /**
     * The local client has lost its connection to an OpenTok session and is trying to reconnect.
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful. (We could show status
     * message for reconnecting/reconnected)
     */
    log.debug('Received OpenTok sessionReconnecting Session event:', event);
  });

  session.on('sessionReconnected', (event) => {
    /**
     * The local client has reconnected to the OpenTok session after its connection was lost
     * temporarily. When the connection is lost, the Session object dispatches a
     * sessionReconnecting event, prior to the sessionReconnected event. If the client cannot
     * reconnect to the session, the Session object dispatches a sessionDisconnected event
     * instead of this event. Any existing publishers and subscribers are automatically
     * reconnected when client reconnects and the Session object dispatches this event.
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful. (We could show status
     * message for reconnecting/reconnected)
     */
    log.debug('Received OpenTok sessionReconnected session event:', event);
  });

  session.on('sessionDisconnected', (event) => {
    /**
     * The client has disconnected from the session. This event may be dispatched asynchronously in
     * response to a successful call to the disconnect() method of the Session object. The event may
     * also be disptached if a session connection is lost inadvertantly, as in the case of a lost
     * network connection. The default behavior is that all Subscriber objects are unsubscribed and
     * removed from the HTML DOM. Each Subscriber object dispatches a destroyed event when the
     * element is removed from the HTML DOM.
     *
     * WEBSDK: Disconnect from video!
     */
    log.debug('Received OpenTok sessionDisconnected Session event:', event);
    handleSessionDisconnected(event.reason);
  });

  session.on('streamCreated', (event) => {
    /**
     * A new stream, published by another client, has been created on this session.
     *
     * WEBSDK: Subscribe to stream!
     */
    log.trace('Received OpenTok streamCreated Session event:', event);
    subscribeToStream(event.stream);
  });

  session.on('streamDestroyed', (event) => {
    /**
     * A stream from another client has stopped publishing to the session.
     * The default behavior is that all Subscriber objects that are subscribed to the stream are
     * unsubscribed and removed from the HTML DOM. Each Subscriber object dispatches a destroyed
     * event when the element is removed from the HTML DOM.
     *
     * WEBSDK: Unsubscribe from stream! Can't do this on Subscriber.destroyed event
     * since no stream info exists at that point.
     */
    log.trace('Received OpenTok streamDestroyed session event:', event);
    unsubscribeFromStream(event.stream);
  });

  session.connect(token, (error) => {
    if (error) {
      handleError(error);
    } else {
      publishToSession(session);
    }
  });
}

function handleSessionConnected() {
  chatState.set('videoConnected', true);
}

function handleSessionDisconnected(reason) {
  resetState();
  chatState.set('videoConnected', false);

  if (reason === NETWORK_DISCONNECTED_REASON) {
    log.debug('OpenTok video disconnected because of network disconnection!');
    /**
     * In WebSDK < 22 we always stopped the Chat Engine video session (chatEngine.stopVideo())
     * when OpenTok session was disconnected due to networkDisconnected. In WebSDK 22 we want
     * to give the visitor a chance to repair the video session, and thus we cannot stop the
     * Chat Engine video session.
     *
     * Is there a risk that we end up with inconsistent states here?
     * Probably not, because of (see comments below)!
     */

    /**
     * OpenTok session disconnected due to network problems. Do not stop Chat Engine video session,
     * instead return a error message and give chat client a chance to repair video.
     */
    sendErrorToClient(errors.NETWORK_ERROR, actions.RELOAD_SESSION);
  } else {
    /**
     * OpenTok session disconnected normally (someone clicked Stop video button). Chat Engine
     * video session will be properly stopped. No need to do anything here!
     */
  }
}

// PUBLISH ----------------------------------------------------------------------

function publishToSession(session) {
  const element = getPublisherElementId();

  const properties = getPublisherProperties();
  log.trace('Publisher properties:', properties);

  const publisher = session.publish(element, properties, (error) => {
    if (error) {
      handleError(error);
    } else {
      syncMuteStateWithPublishedProperties(properties.publishVideo, properties.publishAudio);
      log.debug('Successfully published stream for others to subscribe to!');
    }
  });
  setPublisherState(publisher);

  log.trace('OpenTok Publisher object:', publisher);

  publisher.on('destroyed', (event) => {
    /**
     * Dispatched when the Publisher element is removed from the HTML DOM. When this event is
     * dispatched, you may choose to adjust or remove HTML DOM elements related to the publisher.
     *
     * WebSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.trace('Received OpenTok destroyed Publisher event:', event);
  });

  publisher.on('streamCreated', (event) => {
    /**
     * The publisher has started streaming to the session.
     *
     * WebSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.trace('Received OpenTok streamCreated Publisher event:', event);
  });

  publisher.on('streamDestroyed', (event) => {
    /**
     * The publisher has stopped streaming to the session. The default behavior is that the
     * Publisher object is removed from the HTML DOM. The Publisher object dispatches a destroyed
     * event when the element is removed from the HTML DOM. If you call the preventDefault()
     * method of the event object in the event listener, the default behavior is prevented,
     * and you can, optionally, retain the Publisher for reuse or clean it up using your own code.
     *
     * WebSDK: Reset state publisher!
     */
    log.trace('Received OpenTok streamDestroyed Publisher event:', event);
    setPublisherState(undefined);
  });

  publisher.on('mediaStopped', (event) => {
    /**
     * The user publishing the stream has stopped sharing one or all media types
     * (video, audio and/or screen). This can occur when a user disconnects a camera or microphone
     * used as a media source for the Publisher. Or it can occur when a user closes a when the video
     * and audio sources of the stream are MediaStreamTrack elements and tracks
     * are stopped or destroyed.
     *
     * WebSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.trace('Received OpenTok mediaStopped Publisher event:', event);
  });

  /**
   * publisher.on('videoDimensionsChanged', (event) => {   *
   * Dispatched when the video dimensions of the video change. This can only occur in when the
   * stream.videoType property is set to "screen" (for a screen-sharing video stream), when the
   * user resizes the window being captured. This event object has a newValue property and an
   * oldValue property, representing the new and old dimensions of the video. Each of these has
   * a height property and a width property, representing the height and width, in pixels.
   * WebSDK: NOT NEEDED!
   */

  /**
   * publisher.on('videoElementCreated', (event) => {   *
   * Dispatched when the Publisher's video element is created. Add a listener for this event only
   * if you have set the insertDefaultUI option to false.   *
   * WebSDK: NOT NEEDED!
   */

  /**
   * publisher.on('accessAllowed', (event) => {
   * Dispatched when the user has clicked the Allow button, granting the app access to the
   * camera and microphone. The Publisher object has an accessAllowed property which indicates
   * whether the user has granted access to the camera and microphone.
   * WebSDK: WE PROBABLY DON'T NEED THIS!
   */

  /**
   * publisher.on('accessDenied', (event) => {
   * Dispatched when the user has clicked the Deny button, preventing the app from having access
   * to the camera and microphone.
   * WebSDK: WE PROBABLY DON'T NEED THIS!
   */

  /**
   * publisher.on('accessDialogClosed', (event) => {
   * Dispatched when the Allow/Deny box is closed. (This is the dialog box in which the user can
   * grant the app access to the camera and microphone.)
   * WebSDK: NOT NEEDED!
   */

  /**
   * publisher.on('accessDialogOpened', (event) => {
   * Dispatched when the Allow/Deny dialog box is opened. (This is the dialog box in which the
   * user can grant the app access to the camera and microphone.)
   * WebSDK: NOT NEEDED!
   */
}

function getPublisherProperties() {
  const properties = PUBLISHER_PROPERTIES;

  const invitationNumber = chatState.get('invitationNo');
  properties.name = invitationNumber === 0 ? 'customer' : `invited_${invitationNumber}`;

  // If specific video device has been used earlier, use that one. Otherwise use default one.
  properties.videoSource = state.deviceId || undefined;

  properties.publishVideo = !state.muteVideo;
  properties.publishAudio = !state.muteAudio;

  return properties;
}

function getPublisherElementId() {
  /**
   * Prepare HTML element to be used as video container. OpenTok insertMode parameter has a strange
   * behaviour where element contents cannot be replaced with video contents without the element
   * being deleted (after moving a video chat between two tabs). If 'append' is used instead
   * of 'replace' there will be multiple videos. By using insertMode 'append' and emptying the
   * contents of the element we get a good behaviour.
   */

  const elementId = chatState.get('publisherElement');
  const element = document.getElementById(elementId);

  if (element) {
    // Video element exists on page, remove it before we can append a new video stream!
    element.innerHTML = '';
  }

  return elementId;
}

/**
 * Synchronize mute properties of a video call to reflect the current mute state.
 *
 * Change of mute state can be received from Chat Client when publishing to OT session.
 * If state has changed, mute command need to be re-done after publishing is finished.
 */
function syncMuteStateWithPublishedProperties(publishVideoProperty, publishAudioProperty) {
  const publishVideoState = !state.muteVideo;
  if (publishVideoState !== publishVideoProperty) {
    log.trace('Mute video state was changed during publish operation. Re-do mute command...');
    muteVideo(state.muteVideo);
  }
  const publishAudioState = !state.muteMicrophone;
  if (publishAudioState !== publishAudioProperty) {
    log.trace('Mute microphone state was changed during publish operation. Re-do mute command...');
    muteMicrophone(state.muteMicrophone);
  }
}

// SUBSCRIBE ----------------------------------------------------------------------

function subscribeToStream(stream) {
  log.trace('subscribeToStream:', stream);

  const subscription = findSubscriptionForStream(stream);
  if (!subscription) {
    log.trace('No subscription found!');
    return;
  }

  subscription.subscribing = true;
  subscription.stream = stream;
  subscription.streamId = stream.streamId;
  subscription.name = stream.name;

  const subscriber = state.session.subscribe(
    stream,
    subscription.element,
    SUBSCRIBER_PROPERTIES,
    (error) => {
      if (error) {
        // IMPROVE? Add error handling when joined participants fail to publish! Make sure that
        // it shows somehow in video player GUI so agent and visitor knows there is another part
        // seeing the video call.

        // Only handle errors when subscribing to agent stream! Otherwise a failed stream from a
        // joined part in multi-part chat session could ruin a working video chat between agent and
        // visitor.
        if (doesStreamBelongToAgent(stream)) {
          handleError(error);
        } else {
          log.trace('Ignore subscribe error from streams not belonging to agent!', stream);
        }
      } else {
        log.debug(`Successfully subscribed to stream name: ${stream.name}, id: ${stream.streamId}`);
      }
    }
  );

  log.trace('OpenTok Subscriber object:', subscriber);

  subscription.subscriber = subscriber;

  subscriber.on('connected', (event) => {
    /**
     * Sent when the subscriber's stream has resumed, after the Subscriber dispatches a disconnected
     * event.
     * WEBSDK: Nothing needed to be done here, but logging might be useful. (We could show a status
     * message)
     */
    log.trace('Received OpenTok connected Subscriber event:', event);
  });

  subscriber.on('disconnected', (event) => {
    /**
     * Sent when the subscriber's stream has been interrupted.
     * In response to this event, you may want to provide a user interface notification, to let
     * the user know that the audio-video stream is temporarily disconnected and that the app is
     *  trying to reconnect to it.
     * If the client reconnects to the stream, the Subscriber object dispatches a connected event.
     * Otherwise, if the client cannot reconnect to the stream, the Subscriber object dispatches
     * a destroyed event.
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful. (We could show a status
     * message)
     */
    log.trace('Received OpenTok disconnected Subscriber event:', event);
  });

  subscriber.on('destroyed', (event) => {
    /**
     * Dispatched when the Subscriber element is removed from the HTML DOM. When this event is
     * dispatched, you may choose to adjust or remove HTML DOM elements related to the subscriber.
     * To prevent the Subscriber from being removed from the DOM when the stream is destroyed,
     * listen for the streamDestroyed event dispatched by the Session object. The streamDestroyed
     * event dispatched by the Session object is cancelable, and calling the preventDefault()
     * method of the event object prevents Subscribers of the stream from being removed from the
     * HTML DOM.
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful. (Unsubscribe is done in
     * Session.streamDestroyed event.)
     */
    log.trace('Received OpenTok destroyed Subscriber event:', event);
  });

  subscriber.on('videoDisabled', (event) => {
    /**
     * Dispatched when the video for the subscriber is disabled.
     * WEBSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.debug('Received OpenTok videoDisabled Subscriber event:', event);
  });

  subscriber.on('videoEnabled', (event) => {
    /**
     * Dispatched when the OpenTok Media Router resumes sending video to the subscriber after
     * video was previously disabled.
     * WEBSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.debug('Received OpenTok videoEnabled Subscriber event:', event);
  });

  subscriber.on('videoDisableWarning', (event) => {
    /**
     * Dispatched when the OpenTok Media Router determines that the stream quality has degraded
     * and the video will be disabled if the quality degrades more. If the quality degrades
     * further, the Subscriber disables the video and dispatches a videoDisabled event.
     * By default, the Subscriber displays a video disabled warning indicator when this event
     * is dispatched (and the video is disabled).
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.debug('Received OpenTok videoDisableWarning Subscriber event:', event);
  });

  subscriber.on('videoDisableWarningLifted', (event) => {
    /**
     * Dispatched when the OpenTok Media Router determines that the stream quality has improved
     * to the point at which the video being disabled is not an immediate risk. This event is
     * dispatched after the Subscriber object dispatches a videoDisableWarning event.
     *
     * WEBSDK: Nothing needed to be done here, but logging might be useful.
     */
    log.debug('Received OpenTok videoDisableWarningLifted Subscriber event:', event);
  });

  /**
   * subscriber.on('videoElementCreated', (event) => {
   * Dispatched when the Subscriber's video element is created.
   * Add a listener for this event only if you have set the insertDefaultUI option to false.
   * WEBSDK: NOT NEEDED!
   */
}

function unsubscribeFromStream(stream) {
  log.trace('unsubscribeFromStream:', stream, state.subscriptions);
  for (let i = 0; i < state.subscriptions.length; i++) {
    const subscription = state.subscriptions[i];
    if (subscription.streamId === stream.streamId || subscription.name === stream.name) {
      clearSubscription(subscription);
      break;
    }
  }
}

function initSubscriptions() {
  setSubscriptionsState([]);

  // Agent should be first in subscription list!
  const agentElement = chatState.get('subscriberElement');
  state.subscriptions.push(createSubscription(agentElement, false));

  // Invited parts is rest of list!
  const visitorElements = chatState.get('participantElements');
  visitorElements.forEach((element) => {
    state.subscriptions.push(createSubscription(element, true));
  });
}

function clearSubscriptions() {
  for (let i = 0; i < state.subscriptions.length; i++) {
    clearSubscription(state.subscriptions[i]);
  }
}

function clearSubscription(subscription) {
  if (subscription.subscribing) {
    subscription.stream = undefined;
    subscription.streamId = undefined;
    subscription.subscribing = false;
    subscription.subscriber = undefined;
    subscription.name = undefined;
  }
}

function createSubscription(element) {
  return {
    element,
    stream: undefined,
    streamId: undefined,
    subscribing: false,
    subscriber: undefined,
    name: undefined,
  };
}

function findSubscriptionForStream(stream) {
  // Search for stream in agent subscription (first in array)
  if (doesStreamBelongToAgent(stream)) {
    if (state.subscriptions[0].subscribing) {
      log.trace('Already subscribed to agent stream, ignore it!');
      return undefined;
    }
    return state.subscriptions[0];
  }

  // Search for stream in other participants subscription (index 1+)
  for (let i = 1; i < state.subscriptions.length; i++) {
    if (state.subscriptions[i].subscribing && state.subscriptions[i].name === stream.name) {
      log.trace('Already subscribed to stream, ignore it!');
      return undefined;
    }
  }

  // Search for a free subscription viewport for the participants in the chat (not agent).
  for (let i = 1; i < state.subscriptions.length; i++) {
    if (!state.subscriptions[i].subscribing) {
      return state.subscriptions[i];
    }
  }

  // If not returned by now, we have too many subscribed streams!
  log.error('Receiving more subscribed streams than we can handle!');
  return undefined;
}

// OT EVENT HANDLERS ------------------------------------------------------------------

/**
 * Handle exceptions from OpenTok.
 */
function handleExceptions(event) {
  /**
   * In OpenTok Error objects, .code property is deprecated and .name should be used.
   * OpenTok Exception objects does not have a .name property and .code still has to be used.
   */
  // IMPROVE? Error log some exceptions to Chat Engine?
  switch (event.code) {
    case 2022: // "OT_PROXY_URL_ALREADY_SET_ERROR"
      log.warn(
        'Tried to set OpenTok proxy twice! Proxy should work properly from first setup, but ' +
          'there may be an error in JavaScript code that tries to set proxy twice. Error event:',
        event
      );
      break;
    default:
      log.error('Exception received from OpenTok:', event);
  }
}

// ERROR HANDLING ----------------------------------------------------------------------

function sendErrorToClient(name, action, error = undefined) {
  /**
   * New way of handling video errors (WebSDK 22):
   * - Send a 'info' message to chat client
   * - Expose new API functions to repair a video session
   * - Let chat client choose best way to handle the error
   *
   * Old way of handling video errors (WebSDK < 22):
   * - Send a 'info' message to chat client
   * - Tell chat engine to stop video session
   *
   * Backward compatibility, WebSDK 22 + Refchat < 22:
   * - Video will not be stopped when errors occur
   * - (Broken) video view will still show when errors occur
   * - Visitor need to switch to text chat to see error message
   * - Error message will only be the error name (i.e. "DEVICE_ACCESS_DENIED")
   */

  log.error('Failed setting up video properly! Error:', name, action, error);

  sendInfoEventToChatClient('videoStatus', { type: 'error', name, action, error });
}

function isWebRTCPermissionDismissedError(error) {
  switch (error.originalMessage) {
    case 'Permission dismissed':
      // User closed browser webrtc permission dialog without answering the question!
      // It should be possible to reload opentok publisher and have the user answer again!
      return true;
    default:
      // User answered browser webrtc permission question with a 'deny' answer!
      // User need to manually change or reset the permissions in browser,
      // and then opentok publisher can be reloaded.
      return false;
  }
}

/**
 * Error handler for use in OT completion handlers to functions setting up the video call.
 */
function handleError(error) {
  log.trace('In handleErrors completion handler');
  if (!error) {
    log.trace('No error!');
    return;
  }
  log.trace('Error, handle it...', error);

  // Report errors (reportError) below only for errors
  // where the cause are such that the Support Team may want to troubleshoot it!
  switch (error.name) {
    case 'OT_USER_MEDIA_ACCESS_DENIED':
      if (isWebRTCPermissionDismissedError(error)) {
        sendErrorToClient(errors.DEVICE_ACCESS_DISMISSED, actions.RELOAD_PUBLISHER, error);
      } else {
        sendErrorToClient(errors.DEVICE_ACCESS_DENIED, actions.RELOAD_PUBLISHER, error);
      }
      break;
    case 'OT_NO_VALID_CONSTRAINTS':
      sendErrorToClient(errors.DEVICE_DISABLED, actions.RELOAD_PUBLISHER, error);
      break;
    case 'OT_NO_DEVICES_FOUND':
      sendErrorToClient(errors.DEVICE_NOT_FOUND, actions.RELOAD_PUBLISHER, error);
      break;
    case 'OT_HARDWARE_UNAVAILABLE':
      sendErrorToClient(errors.DEVICE_UNAVAILABLE, actions.RELOAD_PUBLISHER, error);
      break;
    case 'OT_UNABLE_TO_CAPTURE_MEDIA':
    case 'OT_CONSTRAINTS_NOT_SATISFIED':
    case 'OT_NOT_SUPPORTED':
      sendErrorToClient(errors.DEVICE_OTHER_ERROR, actions.RELOAD_PUBLISHER, error);
      break;
    case 'OT_BADLY_FORMED_RESPONSE':
    case 'OT_CONNECT_FAILED':
    case 'OT_CONNECTION_LIMIT_EXCEEDED':
    case 'OT_EMPTY_RESPONSE_BODY':
    case 'OT_TERMS_OF_SERVICE_FAILURE':
    case 'OT_INVALID_HTTP_STATUS':
    case 'OT_XDOMAIN_OR_PARSING_ERROR':
      sendErrorToClient(errors.CONNECT_ERROR, actions.RELOAD_SESSION, error);
      reportError(error);
      break;
    case 'OT_TIMEOUT':
    case 'OT_UNEXPECTED_SERVER_RESPONSE':
    case 'OT_SCREEN_SHARING_NOT_SUPPORTED':
    case 'OT_SCREEN_SHARING_EXTENSION_NOT_REGISTERED':
    case 'OT_SCREEN_SHARING_EXTENSION_NOT_INSTALLED':
      sendErrorToClient(errors.PUBLISH_ERROR, actions.RELOAD_PUBLISHER, error);
      reportError(error);
      break;
    case 'OT_ICE_WORKFLOW_FAILED':
    case 'OT_SET_REMOTE_DESCRIPTION_FAILED':
    case 'OT_STREAM_CREATE_FAILED':
    case 'OT_MEDIA_ERR_ABORTED':
    case 'OT_MEDIA_ERR_DECODE':
    case 'OT_MEDIA_ERR_NETWORK':
    case 'OT_MEDIA_ERR_SRC_NOT_SUPPORTED':
    case 'OT_CREATE_PEER_CONNECTION_FAILED':
      if (error.message.includes('session.publish')) {
        sendErrorToClient(errors.PUBLISH_ERROR, actions.RELOAD_PUBLISHER, error);
      } else {
        sendErrorToClient(errors.SUBSCRIBE_ERROR, actions.RELOAD_SESSION, error);
      }
      reportError(error);
      break;
    case 'OT_STREAM_DESTROYED':
    case 'OT_STREAM_LIMIT_EXCEEDED':
    case 'OT_STREAM_NOT_FOUND':
      sendErrorToClient(errors.SUBSCRIBE_ERROR, actions.RELOAD_SESSION, error);
      reportError(error);
      break;
    case 'OT_AUTHENTICATION_ERROR':
    case 'OT_INVALID_SESSION_ID':
    case 'OT_PERMISSION_DENIED':
      sendErrorToClient(errors.SESSION_ERROR, actions.RENEW_SESSION, error);
      reportError(error);
      break;
    case 'OT_NOT_CONNECTED':
      sendErrorToClient(errors.NETWORK_ERROR, actions.RELOAD_SESSION, error);
      break;
    case 'OT_CHROME_MICROPHONE_ACQUISITION_ERROR':
      sendErrorToClient(errors.BROWSER_ERROR, actions.RESTART_BROWSER, error);
      break;
    case 'OT_INVALID_PARAMETER':
      sendErrorToClient(errors.INTERNAL_ERROR, actions.RELOAD_PAGE, error);
      reportError(error);
      break;
    default:
      log.error(`Unrecognized OpenTok error: ${error.name}, error:`, error);
      sendErrorToClient(errors.UNEXPECTED_ERROR, actions.RELOAD_PAGE, error);
      reportError(error);
  }
}

// CHAT CLIENT COMMUNICATION ----------------------------------------------------------------------

function initChatClient(infoText, agent) {
  // Make sure chat client shows video view while setting up video!
  callEventHandler('showVideo', { infoText, agent });
}

function tellChatClientToStopVideo() {
  callEventHandler('stopVideo', { infoText: '' });
}

function tellChatClientToSuspendVideo(infoText, agent) {
  const params = { infoText, agent, showVideoCallback: takeBackSuspendedVideoWindow };
  callEventHandler('videoStandby', params);
}

function sendInfoEventToChatClient(infoKey, data) {
  callEventHandler('info', { infoKey, data });
}

// VIDEO WINDOW CONTROLLER ---------------------------------------------------------------------

function initVideoWindowController() {
  videoWindowController.registerSomeoneElseTookIt(someoneElseTookVideoWindowCallback);
}

export function disconnectVideoWindowController() {
  videoWindowController.noVideo();
}

export function takeVideoWindow() {
  log.trace('Take video window!');
  videoWindowController.iTakeIt();
}

function someoneElseTookVideoWindowCallback() {
  log.trace('Someone else took video window!');
  if (state.session) {
    disconnectSession();
  }
  // Agent unknown here, use undefined to not overwrite old agent value in chat client!
  tellChatClientToSuspendVideo('', undefined);
}

async function takeBackSuspendedVideoWindow() {
  log.trace('Take back suspended video window!');

  takeVideoWindow();

  // Agent unknown here, use undefined to not overwrite old agent value in chat client!
  await setupVideo({ infoText: '', agent: undefined });
}

// LOGGING ----------------------------------------------------------------------

/**
 * Initialize OpenTok logging.
 */
function initLogging() {
  const config = { DEBUG: OT.DEBUG, INFO: OT.INFO, WARN: OT.WARN, ERROR: OT.ERROR };
  const logLevelParam = getUrlParam(OPENTOK_LOG_LEVEL_URL_PARAM_NAME)?.toUpperCase() || '';
  const logLevel = config[logLevelParam] || OT.NONE;
  OT.setLogLevel(logLevel);
}

// STATE ----------------------------------------------------------------------

/**
 * Initialisation of state is done every time video chat is started, and thus also every time the
 * chat page is reloaded. State variables that should survive a page reload should be fetched from
 * persistent state.
 */
async function initState() {
  setSessionState(undefined);
  setPublisherState(undefined);
  setSubscriptionsState([]);
  setMuteVideoState((await getFromPeristentState('mute_video')) || false);
  setMuteMicrophoneState((await getFromPeristentState('mute_microphone')) || false);
  setDeviceIdState(await getFromPeristentState('device_id'));
  log.trace('State initialized:', state);
}

/**
 * Reset of state is done every time video chat is ended.
 * State variables that should survive to next video chat should NOT be reset!
 */
async function resetState() {
  setSessionState(undefined);
  setPublisherState(undefined);
  setSubscriptionsState([]);
  // MuteVideo and MuteMicrophone should always default to false when starting a new video chat.
  // Since they live in persistent state (local storage) they have to be reset.
  setMuteVideoState(false);
  setMuteMicrophoneState(false);
  // DeviceId should default to last used device when starting a new video chat,
  // and should thus NOT be reset!
  log.trace('State reset:', state);
}

function setSessionState(value) {
  state.session = value;
  log.trace('state.session modified:', state.session);
}

function setPublisherState(value) {
  state.publisher = value;
  log.trace('state.publisher modified:', state.publisher);
}

function setSubscriptionsState(value) {
  state.subscriptions = value;
  log.trace('state.subscriptions modified:', state.subscriptions);
}

function setMuteVideoState(value) {
  state.muteVideo = value;
  setInPeristentState('mute_video', value);
  log.trace('state.muteVideo modified:', state.muteVideo);
}

function setMuteMicrophoneState(value) {
  state.muteMicrophone = value;
  setInPeristentState('mute_microphone', value);
  log.trace('state.muteMicrophone modified:', state.muteMicrophone);
}

function setDeviceIdState(value) {
  state.deviceId = value;
  setInPeristentState('device_id', value);
  log.trace('state.deviceId modified:', state.deviceId);
}

// HELPER FUNCTIONS ----------------------------------------------------------------------

function setInPeristentState(key, value) {
  setXDItem('CGWebSDK', 'local', key, value);
}

async function getFromPeristentState(key) {
  return getXDItem('CGWebSDK', 'local', key);
}

function doesStreamBelongToAgent(stream) {
  if (!stream || !stream.connection) {
    return false;
  }
  /**
   * Explanation why undefined stream name belongs to agent:
   * "Från och med att vi införde flerpartschatt började vi tagga upp strömmarna med 'agent' och
   * 'customer' samt 'invited_N'. Om strömmen inte har något namn innebär det att den är skapad med
   * Interact eller Web SDK som är från en release före inbjuden chatt. Och om det inte finns
   * inbjudna måste strömmen komma från agenten."
   */
  if (typeof stream.name === 'undefined') {
    return true;
  }
  // Newer Agent clients use stream.name property to identify themselves!
  if (stream.name === 'agent') {
    return true;
  }
  // Older Agent clients use stream.connection.data to identify themselves! (BUG ALM-18799)
  if (stream.connection.data === 'agent') {
    return true;
  }

  return false;
}

function reportError(error = undefined) {
  OT.reportIssue((issueError, issueId) => {
    if (issueError) {
      log.error('Failed to report video error to OpenTok. Error:', issueError);
      return;
    }
    const logMsg = error
      ? `Video error reported to OpenTok. Error name: ${error.name}; Error message: ${error.message}; ApiKey: ${state.session?.apiKey}; SessionId: ${state.session?.id}; User: ${state.session?.connection?.data}; Issue ID: ${issueId}`
      : `Video error reported to OpenTok. ApiKey: ${state.session?.apiKey}; SessionId: ${state.session?.id}; User: ${state.session?.connection?.data}; Issue ID: ${issueId}`;
    log.error(logMsg);

    if (getConfig('cht').video.logging.logErrorsToChatEngine) {
      log.trace(`Log error data to Chat Engine log file: ${logMsg}`);
      chatEngine.chatEngineLog(logMsg, false);
    }
  });
}
